import { Announcement } from '@servicetitan/design-system';
import useAnnouncements from '../hooks/useAnnouncements';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import useService from '@modules/ServiceModule/useService';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { hasValidSeat } from '@constants/user';
import useCallingDisabled from '@lib/hooks/useCallingDisabled';
import { useFeatureFlags, FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { strings } from '../strings';
import SoftPhoneModule, { useAudioDevices } from '@modules/SoftPhoneModule';
import { phoneState } from '@modules/SoftPhoneModule/state';
import {
  LegacyJwtFetchStatus,
  WebphoneConnectionStatus,
} from '@modules/SoftPhoneModule/types';

/**
 * Responsible for reading the announcements from the AnnouncementsModule and rendering them.
 */
const Announcements = () => {
  const { announcements } = useAnnouncements();
  const {
    phoneConnected,
    registrationOverwritten,
    disconnectTriggeredLocally,
    existingRegistrationsDetected,
    isUnactivatedImpersonator,
    connectionStatus,
    legacyJwtFetchStatus,
  } = usePhoneState();
  const { serviceConnected } = useService();
  const { user } = useAuth();
  const { callingDisabled } = useCallingDisabled();
  const {
    [FeatureFlagKeys.featurePreventMultipleRegistrations]:
      preventMultipleRegistrationsEnabled,
    [FeatureFlagKeys.featureRtcWebphoneHandlesFetchingJwt]:
      rtcWebphoneHandlesFetchingJwt,
    [FeatureFlagKeys.featureAudioDeviceManagement]:
      featureAudioDeviceManagement,
  } = useFeatureFlags();
  const {
    errorCategory,
    isDevicesReady,
    audioInputDevices,
    dismissedInitFailed,
    dismissedHotUnplug,
  } = useAudioDevices();

  const handleActivateWebPhone = () => {
    SoftPhoneModule.activateWebPhone();
  };

  const handleImpersonationActivatePhone = () => {
    phoneState.isUnactivatedImpersonator = false;
  };

  const handleRetryDeviceInitialization = () => {
    SoftPhoneModule.retryDeviceInitialization();
  };

  const handleDismissAudioInitFailedBanner = () => {
    SoftPhoneModule.dismissAudioInitFailedBanner();
  };

  const handleDismissAudioHotUnplugBanner = () => {
    SoftPhoneModule.dismissAudioHotUnplugBanner();
  };

  const hasActiveRegistrationElsewhere =
    existingRegistrationsDetected || registrationOverwritten;

  const callingEnabled = !callingDisabled;
  const hasSeat = hasValidSeat(user?.seatLevel);

  const isOverWritingRegistration = phoneState.isOverWritingRegistration;

  const hasConnectionError =
    connectionStatus === WebphoneConnectionStatus.RegistrationFailed ||
    connectionStatus === WebphoneConnectionStatus.WebphoneJwtFetchFailed ||
    connectionStatus === WebphoneConnectionStatus.WebphoneJwtNotAvailable;

  return (
    <>
      {/*preventMultipleRegistrationsEnabled ON */}
      {preventMultipleRegistrationsEnabled &&
        !phoneConnected &&
        hasActiveRegistrationElsewhere && (
          <Announcement
            status='warning'
            title={strings.noActiveWebphone}
            actionName={strings.noActiveWebphoneAction}
            onActionClick={handleActivateWebPhone}
          />
        )}
      {/* preventMultipleRegistrationsEnabled OFF */}
      {!preventMultipleRegistrationsEnabled &&
        !phoneConnected &&
        registrationOverwritten && (
          <Announcement
            status='warning'
            title={strings.registrationOverwritten}
          />
        )}
      {/* Connection error announcement */}
      {callingEnabled &&
        !phoneConnected &&
        hasConnectionError &&
        !hasActiveRegistrationElsewhere && (
          <Announcement
            status='critical'
            title={strings.webPhoneConnectionError}
            actionName={strings.webPhoneConnectAction}
            onActionClick={handleActivateWebPhone}
          />
        )}
      {/* Legacy JWT fetch failure — only when host app is responsible for JWT fetching */}
      {callingEnabled &&
        !rtcWebphoneHandlesFetchingJwt &&
        legacyJwtFetchStatus === LegacyJwtFetchStatus.Failed && (
          <Announcement
            status='critical'
            title={strings.webPhoneConnectionError}
            actionName={strings.webPhoneConnectAction}
            onActionClick={() => SoftPhoneModule.retryLegacyJwtFetch()}
          />
        )}
      {hasSeat &&
        !phoneConnected &&
        serviceConnected &&
        !hasActiveRegistrationElsewhere &&
        !disconnectTriggeredLocally &&
        !hasConnectionError &&
        user?.isImpersonated &&
        isUnactivatedImpersonator && (
          <Announcement
            status='warning'
            title={strings.webPhoneImpersonating}
            actionName={strings.noActiveWebphoneAction}
            onActionClick={handleImpersonationActivatePhone}
          />
        )}
      {/* Connecting announcement when registration is being overwritten */}
      {isOverWritingRegistration &&
        connectionStatus === WebphoneConnectionStatus.Connecting && (
          <Announcement status='warning' title={strings.webPhoneReconnecting} />
        )}
      {/* Scenario 1: device initialization failed with no devices found */}
      {featureAudioDeviceManagement &&
        callingEnabled &&
        errorCategory === 'device_not_found' &&
        !isDevicesReady &&
        !dismissedInitFailed && (
          <Announcement
            status='warning'
            title={strings.noAudioDevices}
            actionName={strings.noAudioDevicesRetryAction}
            onActionClick={handleRetryDeviceInitialization}
            onClose={handleDismissAudioInitFailedBanner}
          />
        )}
      {/* Scenario 2: devices were ready but microphone has been hot-unplugged */}
      {featureAudioDeviceManagement &&
        callingEnabled &&
        isDevicesReady &&
        audioInputDevices.length === 0 &&
        !dismissedHotUnplug && (
          <Announcement
            status='warning'
            title={strings.noAudioDevices}
            onClose={handleDismissAudioHotUnplugBanner}
          />
        )}
      {Object.entries(announcements).map(([key, announcement]) => (
        <Announcement {...announcement} key={key} />
      ))}
    </>
  );
};

export default Announcements;
